import React from 'react'
import { CheckCircle2 } from 'lucide-react'
import type { OpportunitySignals } from '../services/signalsService'
import { cn } from '@/lib/utils'
import { Tooltip } from '@/shared/ui'
import { computeEntryRangeInfo, isPriceInEntryRange } from '../utils/entryRange'
import { computeTargetPercents } from '../utils/pnl'

type Props = {
  signals: OpportunitySignals | null
  lastPrice?: number | null
  dateText?: string | null
  size?: 'default' | 'compact'
  footerRight?: React.ReactNode
}

export function SignalCard({
  signals,
  lastPrice,
  dateText,
  size = 'default',
  footerRight,
}: Props) {
  if (!signals) return null

  const {
    tp1: rawTp1,
    tp2: rawTp2,
    tp3: rawTp3,
    stopAggressive,
    stopConservative,
    tp1Hit,
    tp2Hit,
    tp3Hit,
    stopHit: rawStopHit,
  } = signals

  const fmt = (v: number | null): string => {
    if (v == null || !Number.isFinite(v)) return '-'
    const abs = Math.abs(v)

    // Regras de casas decimais para o card de oportunidades:
    // - 3 ou mais dígitos antes do ponto (>= 100): 2 casas decimais
    // - 2 dígitos antes do ponto (>= 10 e < 100): 3 casas decimais
    // - 1 dígito antes do ponto (< 10): 4 casas decimais
    let decimals = 4
    if (abs >= 100) {
      decimals = 2
    } else if (abs >= 10) {
      decimals = 3
    }

    return v.toFixed(decimals)
  }

  const sameStop =
    stopAggressive != null &&
    stopConservative != null &&
    Math.abs(stopAggressive - stopConservative) < 1e-12

  const entryInfo = computeEntryRangeInfo(signals)
  const { hasEntryRange, entryFrom, entryTo } = entryInfo

  const { tp1Pct, tp2Pct, tp3Pct, stopBasePct, stopAggPct, stopConsPct } =
    computeTargetPercents(signals)

  const tp1 = rawTp1 ?? null
  const tp2 = rawTp2 ?? null
  const tp3 = rawTp3 ?? null

  const tp1HitNorm = !!tp1Hit
  const tp2HitNorm = !!tp2Hit
  const tp3HitNorm = !!tp3Hit

  const price = typeof lastPrice === 'number' ? lastPrice : null
  const inEntryRange = isPriceInEntryRange(entryInfo, price)

  const stopHit = !!rawStopHit
  const anyTargetOrStopHit = tp1HitNorm || tp2HitNorm || tp3HitNorm || stopHit

  const showEntryHint = hasEntryRange && inEntryRange && !anyTargetOrStopHit

  let dateLabel: string | null = null
  let timeLabel: string | null = null
  if (dateText) {
    const parts = dateText.split(',')
    const rawDate = parts[0]
    const rawTime = parts.length > 1 ? parts[1] : null
    dateLabel = (rawDate || '').trim() || dateText
    timeLabel = rawTime ? rawTime.trim() : null
  }

  const rootTextClass =
    size === 'compact'
      ? 'text-[10px] sm:text-[10px] md:text-[11px]'
      : 'text-[11px] sm:text-[11px] md:text-[11px]'

  const targetsTextClass =
    size === 'compact'
      ? 'text-[9px] sm:text-[10px] md:text-[11px]'
      : 'text-[9px] sm:text-[11px] md:text-[11px]'

  const targetsTextClassWithBadge = footerRight
    ? 'text-[9px] sm:text-[10px] md:text-[10px]'
    : targetsTextClass

  const formatPct = (pct: number | null): string | null =>
    pct != null && Number.isFinite(pct) ? `${pct.toFixed(2)}%` : null

  const tp1PctLabel = formatPct(tp1Pct)
  const tp2PctLabel = formatPct(tp2Pct)
  const tp3PctLabel = formatPct(tp3Pct)
  const stopBasePctLabel = formatPct(stopBasePct)
  const stopAggPctLabel = formatPct(stopAggPct)
  const stopConsPctLabel = formatPct(stopConsPct)

  const stopContent = sameStop ? (
    <span
      className={cn(
        stopAggressive != null || stopConservative != null
          ? 'text-red-400'
          : 'text-foreground/50',
      )}
    >
      {fmt(stopAggressive ?? stopConservative ?? null)}
      {(stopBasePctLabel || stopAggPctLabel || stopConsPctLabel) &&
        ` (${stopBasePctLabel || stopAggPctLabel || stopConsPctLabel})`}
    </span>
  ) : (
    <>
      <span className="text-foreground/70">Agressivo:</span>
      <span
        className={cn(
          stopAggressive != null ? 'text-red-400' : 'text-foreground/50',
        )}
      >
        {fmt(stopAggressive)}
        {stopAggPctLabel && ` (${stopAggPctLabel})`}
      </span>
      <span className="text-foreground/40">|</span>
      <span className="text-foreground/70">Conservador:</span>
      <span
        className={cn(
          stopConservative != null ? 'text-red-400' : 'text-foreground/50',
        )}
      >
        {fmt(stopConservative)}
        {stopConsPctLabel && ` (${stopConsPctLabel})`}
      </span>
    </>
  )

  return (
    <div className={cn('flex flex-col text-foreground', rootTextClass)}>
      <div className="grid grid-cols-6 items-stretch gap-2 py-1">
        <div className="col-span-2 flex min-w-0 flex-col gap-1 pr-1">
          <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
            <span className="font-semibold">Zona técnica</span>
            {showEntryHint && (
              <Tooltip content="Neste momento, o preço está dentro da zona de entrada. Se for entrar, este é o momento técnico. Lembre-se de que a decisão é sua e exige consciência dos riscos envolvidos. Ajuste posição, stop e gerenciamento conforme seu plano individual.">
                <span className="text-[10px] font-medium text-lime-400">
                  (no range)
                </span>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-nowrap items-center gap-1">
            {hasEntryRange ? (
              <>
                <span className="inline-flex items-center rounded-md bg-[#232323] px-2 py-[2px] whitespace-nowrap text-[9px] sm:text-[10px] md:text-[11px]">
                  {fmt(entryFrom)}
                </span>
                <span className="inline-flex items-center rounded-md bg-[#232323] px-2 py-[2px] whitespace-nowrap text-[9px] sm:text-[10px] md:text-[11px]">
                  {fmt(entryTo)}
                </span>
              </>
            ) : (
              <span>-</span>
            )}
          </div>
        </div>
        <div className="col-span-2 flex min-w-0 flex-col gap-1 border-l border-[#161616] px-2">
          <span className="font-semibold">Invalidação</span>
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-[#232323] px-2 py-[2px] text-[9px] sm:text-[10px] md:text-[11px]">
              {stopContent}
            </span>
          </div>
        </div>
        <div className="col-span-2 flex min-w-0 flex-col justify-center gap-1 border-l border-[#161616] pl-2">
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] sm:text-[11px] text-[#454545]">
            <div className="flex min-w-0 flex-col">
              <span className="font-semibold">Data</span>
              <span className="truncate">
                {dateLabel || '-'}
              </span>
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="font-semibold">Horário</span>
              <span className="truncate">
                {timeLabel || (dateLabel ? '-' : '')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[5px] p-[5px]">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Projeções:</span>
          {footerRight && (
            <span className="ml-auto flex items-center">{footerRight}</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'flex items-center gap-1 max-w-full whitespace-normal md:whitespace-nowrap rounded-md bg-[#232323] px-2 py-[2px]',
              targetsTextClassWithBadge,
            )}
          >
            <span
              className={cn(
                tp1 != null ? 'text-emerald-400' : 'text-foreground/50',
                tp1HitNorm && 'line-through text-foreground/60',
              )}
            >
              1- {fmt(tp1)}
              {tp1PctLabel && ` (${tp1PctLabel})`}
            </span>
            {tp1HitNorm && <CheckCircle2 className="h-3 w-3 text-amber-400" />}
          </span>
          <span
            className={cn(
              'flex items-center gap-1 max-w-full whitespace-normal md:whitespace-nowrap rounded-md bg-[#232323] px-2 py-[2px]',
              targetsTextClassWithBadge,
            )}
          >
            <span
              className={cn(
                tp2 != null ? 'text-emerald-400' : 'text-foreground/50',
                tp2HitNorm && 'line-through text-foreground/60',
              )}
            >
              2- {fmt(tp2)}
              {tp2PctLabel && ` (${tp2PctLabel})`}
            </span>
            {tp2HitNorm && <CheckCircle2 className="h-3 w-3 text-amber-400" />}
          </span>
          <span
            className={cn(
              'flex items-center gap-1 max-w-full whitespace-normal md:whitespace-nowrap rounded-md bg-[#232323] px-2 py-[2px]',
              targetsTextClassWithBadge,
            )}
          >
            <span
              className={cn(
                tp3 != null ? 'text-emerald-400' : 'text-foreground/50',
                tp3HitNorm && 'line-through text-foreground/60',
              )}
            >
              3- {fmt(tp3)}
              {tp3PctLabel && ` (${tp3PctLabel})`}
            </span>
            {tp3HitNorm && <CheckCircle2 className="h-3 w-3 text-amber-400" />}
          </span>
        </div>
      </div>
    </div>
  )
}
